import Icon, {
  AppstoreOutlined,
  MailOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as overview } from 'assets/svgs/overview.svg';
import { ReactComponent as receiptLong } from 'assets/svgs/receiptLong.svg';
import { ReactComponent as supportAgent } from 'assets/svgs/supportAgent.svg';
import { HeaderEnum } from 'enums/header.enum';
import { getRefreshTokenService, setLogout } from 'services/config';
import { logoutSession } from 'services/sessions/SessionsService';
import * as BuyIntentionCalls from 'store/state/buyIntention/apiCalls';
import { sessionSelectors } from 'store/state/selectors';
import { buyIntentionActions, sessionActions } from 'store/state/slices';
import {
  FeatureFlags,
  useFeatureFlagStore,
} from 'zustand/featureFlag/useFeatureFlagStore';

export const SSO_APPS_URL = process.env.REACT_APP_SSO_APPS ?? '';

export interface IUserMenuItem {
  key: string;
  label: string;
  icon: ReactNode;
  external: boolean;
  onClick: () => void;
}

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export function useUserMenuItems() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { producerUrl } = useSelector(sessionSelectors.getRoot);
  const isProducerAuthenticated = useSelector(
    sessionSelectors.isProducerAuthenticated,
  );
  const { t } = useTranslation('translation', { keyPrefix: 'header' });

  const isFeatureFlagEnabled = useFeatureFlagStore(
    (state) => state.isFeatureFlagEnabled,
  );
  const isDeskManagerServiceRequestsEnabled = isFeatureFlagEnabled(
    FeatureFlags.DeskManagerServiceRequestsOnECommerce,
  );

  const goTo = (path: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${producerUrl}/${path}`);
  };

  const ordersItems: IUserMenuItem[] = [
    ...(isProducerAuthenticated
      ? []
      : [
          {
            key: 'my-orders',
            label: t('myOrders'),
            icon: <Icon component={overview} />,
            external: false,
            onClick: () => goTo('my-orders'),
          },
        ]),
    {
      key: 'my-order-intentions',
      label: t('myOrderIntentions'),
      icon: <Icon component={receiptLong} />,
      external: false,
      onClick: () => goTo('my-order-intentions'),
    },
    {
      key: 'register-order-intention',
      label: t('registerOrderIntention'),
      icon: <ShoppingCartOutlined />,
      external: false,
      onClick: () => {
        dispatch(buyIntentionActions.setShowIntentionModal(true));
        dispatch(BuyIntentionCalls.fetchProducers());
      },
    },
    {
      key: 'my-apps',
      label: t('myApps'),
      icon: <AppstoreOutlined />,
      external: true,
      onClick: () => openInNewTab(SSO_APPS_URL),
    },
  ];

  const supportItems: IUserMenuItem[] = [
    {
      key: 'open-requests',
      label: t('openRequests'),
      icon: <Icon component={supportAgent} />,
      external: !isDeskManagerServiceRequestsEnabled,
      onClick: () => {
        if (!isDeskManagerServiceRequestsEnabled) {
          window.open(HeaderEnum.LINKSUPPORT);
        } else {
          goTo('service-request');
        }
      },
    },
    {
      key: 'veiling-contact',
      label: t('veilingContact'),
      icon: <MailOutlined />,
      external: true,
      onClick: () => openInNewTab(HeaderEnum.LINKBUSINESSCONTACTS),
    },
  ];

  const logout = useCallback(async () => {
    const refresh = getRefreshTokenService();
    if (refresh) {
      try {
        await logoutSession(refresh);
      } catch {
        // best-effort: continua mesmo se backend falhar
      }
    }
    setLogout();
    dispatch(sessionActions.logout());
    navigate('/logout');
  }, [dispatch, navigate]);

  return { ordersItems, supportItems, logout };
}
